import ky, { type Hooks } from 'ky';

import { isNumber } from '@enterprise/core';

import { logError } from './hooks/log-error';
import { logRequest } from './hooks/log-request';
import { logResponse } from './hooks/log-response';

/**
 * Creates a configured Ky API client instance with custom hooks and progress logging.
 *
 * @param baseUri - The base URL or string to be used as the prefix for all requests.
 * @returns A Ky API client instance with predefined headers, hooks for logging requests, responses, and errors,
 *          and progress logging for uploads and downloads.
 *
 * @remarks
 * - The client sets `Accept` and `Content-Type` headers for JSON communication.
 * - Hooks are used to log requests, responses, and errors via custom functions.
 * - Progress for uploads and downloads is logged to the console.
 */
export const createApi = (
  baseUri?: URL | string,
  hooks: Hooks = {
    afterResponse: [],
    beforeError: [],
    beforeRequest: [],
    beforeRetry: [],
  }
) => {
  const setting = import.meta.env.VITE_APP_MAX_RETRY_ATTEMPTS;
  const parsedRetryAttempts = isNumber(setting) ? Number.parseInt(setting, 10) : Number.NaN;
  const maxRetryAttempts = Number.isFinite(parsedRetryAttempts) && parsedRetryAttempts >= 0 ? parsedRetryAttempts : 3;
  const limit = Math.max(maxRetryAttempts - 1, 0);

  return ky.create({
    baseUrl: baseUri,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    hooks: {
      afterResponse: [logResponse, ...(hooks.afterResponse ?? [])],
      beforeError: [logError, ...(hooks.beforeError ?? [])],
      beforeRequest: [logRequest, ...(hooks.beforeRequest ?? [])],
      beforeRetry: [...(hooks.beforeRetry ?? [])],
    },
    retry: {
      limit,
      statusCodes: [408, 413, 429, 500, 502, 503, 504],
    },
  });
};
